/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useEffect } from "react";
import { Button, Label } from "reactstrap";
import { EditorState, convertToRaw, ContentState, convertFromHTML } from 'draft-js';
import CenterModal from "./CenterModal";
import draftToHtml from 'draftjs-to-html';
import useUpdateState from "../util/useUpdateState";
import TextEditor from "./TextEditor";

export const defaultHTML = "<h3>Sample Title</h3><p>Sample Body</p>";

const ModalTextEditor = ({
  isOpen,
  toggle,
  title = "Text Editor",
  initialHTML,
  forceSetHTML,
  onCancel,
  onUpdate,
  onDone
}) => {
  // state
  const [state, , updateState] = useUpdateState({
    textMode: true,
    editorState: EditorState.createEmpty(),
    htmlContent: ""
  });

  const htmlToEditorState = (html) => {
    const blocksFromHTML = convertFromHTML(html);
    const contentState = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );
    return EditorState.createWithContent(contentState);
  };

  useEffect(() => {
    if (isOpen) {
      updateState({ editorState: htmlToEditorState(initialHTML) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialHTML]);

  // handlers
  const handleCancel = () => {
    // handle callbacks
    onDone?.();
    onCancel?.();

    // reset html state
    updateState({ editorState: htmlToEditorState(initialHTML) });
  };

  const handleForceSet = () => {
    // force html state
    updateState({
      textMode: true,
      editorState: htmlToEditorState(forceSetHTML)
    });
  };

  const handleUpdate = () => {
    // handle callbacks
    onDone?.();
    const html = state.textMode
      ? draftToHtml(convertToRaw(state.editorState.getCurrentContent()))
      : state.htmlContent;
    onUpdate?.(html);
  };

  return (
    <CenterModal isOpen={isOpen} toggle={toggle} size="xl">
      <Label className="w-100 text-center" tag="h2">{title}</Label>
      <TextEditor state={state} updateState={updateState} />
      <div className="pt-3 d-flex justify-content-between">
        <Button onClick={handleCancel}>Cancel</Button>
        {forceSetHTML && <Button color="info" onClick={handleForceSet}>Paste Default</Button>}
        <Button onClick={handleUpdate} color="success">Update</Button>
      </div>
    </CenterModal>
  );
};

export default ModalTextEditor;
